import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";
import { app as teamsApp, authentication as teamsAuthentication } from "@microsoft/teams-js";

const TENANT_ID = "e0961260-4859-48cc-935f-e0e51b53dd89";
const CLIENT_ID = "085c4050-557e-4b32-a740-0ffb73d91ff6";
const API_SCOPE = `api://${CLIENT_ID}/access_as_user`;
const TEAMS_INIT_TIMEOUT_MS = 1_500;

export const AUTH_REQUIRED_MESSAGE = "サインインが必要です";

export class AuthRequiredError extends Error {
  constructor() {
    super(AUTH_REQUIRED_MESSAGE);
    this.name = "AuthRequiredError";
  }
}

const msal = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: `${window.location.origin}/auth-callback/`,
  },
  cache: { cacheLocation: "sessionStorage" },
});

let msalInitialization: Promise<void> | undefined;
let teamsAvailability: Promise<boolean> | undefined;

async function initializeMsal(): Promise<void> {
  msalInitialization ??= msal.initialize();
  await msalInitialization;
}

async function isRunningInTeams(): Promise<boolean> {
  teamsAvailability ??= Promise.race([
    teamsApp.initialize().then(() => true),
    new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), TEAMS_INIT_TIMEOUT_MS)),
  ]).catch(() => false);
  return teamsAvailability;
}

async function teamsToken(): Promise<string | undefined> {
  if (!(await isRunningInTeams())) return undefined;
  try {
    return await teamsAuthentication.getAuthToken();
  } catch {
    throw new AuthRequiredError();
  }
}

function currentAccount(): AccountInfo | undefined {
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0];
}

async function browserToken(interactive: boolean): Promise<string> {
  await initializeMsal();
  let account = currentAccount();
  if (!account) {
    if (!interactive) throw new AuthRequiredError();
    const login = await msal.loginPopup({ scopes: [API_SCOPE] });
    account = login.account;
    msal.setActiveAccount(account);
    return login.accessToken;
  }

  try {
    return (await msal.acquireTokenSilent({ account, scopes: [API_SCOPE] })).accessToken;
  } catch (error) {
    if (!interactive || !(error instanceof InteractionRequiredAuthError)) {
      throw new AuthRequiredError();
    }
    return (await msal.acquireTokenPopup({ account, scopes: [API_SCOPE] })).accessToken;
  }
}

export async function getAccessToken(interactive = false): Promise<string> {
  return (await teamsToken()) ?? browserToken(interactive);
}

export async function signIn(): Promise<void> {
  await getAccessToken(true);
}
